import { useLocation } from 'react-router-dom';

import { useActivation } from '../ActivationContext';

import { isAllowedAppHandoffTarget } from '@/utils/urlHelpers';
import { REDIRECT_TO_SESSION_KEY, CANOPY_POST_AUTH_CALLBACK } from '@/utils/constants';
import { logError } from '@/utils/logError';

/**
 * Where to send the member's device once authentication finishes.
 *
 * Looked up rather than held in flow state, because a member arriving from an emailed
 * link lands in a fresh browser tab — any in-memory state from before the email was sent
 * is gone. So the address is looked for in three places, most specific first: this
 * screen's own URL, the copy saved when the member first arrived, and whatever flow state
 * still holds.
 *
 * Every candidate is checked with `isAllowedAppHandoffTarget`, not a scheme check — the
 * auth service refuses anything but an exact match, so a looser check here would relay an
 * address the service bounces, costing the member the email entirely. A candidate that
 * fails is ignored, not relayed, and logged, so a dropped address is visible rather than
 * silent.
 *
 * Two forms, differing only in what happens when nothing is found:
 *
 * - `appSuppliedRedirectTo` returns nothing. Use when relaying to the auth service, where
 *   absent has to stay absent.
 * - `resolvePostAuthRedirectTo` returns the app's one post-authentication address. Use at
 *   the end of the flow, where some address has to be chosen — a default, not a guess at
 *   who the member is.
 */

interface RedirectToSources {
  /** The current screen's query string, e.g. `useLocation().search`. */
  search: string;
  /** Whatever the activation flow's state still holds, when a caller has that state. */
  fromFlowState?: string | null;
}

/**
 * The scheme of a value, for logging. Never log the value itself — a rejected address can
 * carry arbitrary query params, and this runs on paths a member's own data passes through.
 */
const schemeOf = (value: string): string => {
  const idx = value.indexOf(':');
  return idx === -1 ? '(none)' : value.slice(0, idx + 1);
};

/**
 * Values already reported. These resolvers are called from render, so without this a single
 * screen would log once per frame. Page-scoped and bounded by the number of distinct bad
 * values a page sees, which is one in every case we know of.
 */
const reportedRejections = new Set<string>();
let reportedDefaultFallback = false;

const reportRejected = (field: string, value: string): void => {
  const seen = `${field}:${value}`;
  if (reportedRejections.has(seen)) { return; }
  reportedRejections.add(seen);
  logError(
    '[PostAuthRedirect]',
    'ignored a redirect address that is not one of the app handoff targets',
    undefined,
    { level: 'warning', extra: { field, scheme: schemeOf(value) } },
  );
};

/**
 * The address the app actually asked for, or nothing at all.
 *
 * Returning nothing matters when we relay this to the auth service: claiming an app
 * address for a member who is signing in on a desktop browser would put a private app
 * link in their email that their machine cannot open. Absent means absent.
 */
export const appSuppliedRedirectTo = (
  { search, fromFlowState }: RedirectToSources,
): string | undefined => {
  const fromUrl = new URLSearchParams(search).get('redirectTo');
  if (fromUrl) {
    if (isAllowedAppHandoffTarget(fromUrl)) { return fromUrl; }
    reportRejected('url', fromUrl);
  }

  // Guarded because this also runs in a render path. A browser with storage locked down
  // throws on access, and there is no error boundary in this app — an unguarded read here
  // blanks the screen mid-flow. Treat unreadable storage as "no saved value" and fall
  // through to the remaining candidates.
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);
  } catch {
    stored = null;
  }
  if (stored) {
    if (isAllowedAppHandoffTarget(stored)) { return stored; }
    // Reachable without an attacker: this key is shared with the Spark flows.
    reportRejected('session_storage', stored);
  }

  if (fromFlowState) {
    if (isAllowedAppHandoffTarget(fromFlowState)) { return fromFlowState; }
    reportRejected('flow_state', fromFlowState);
  }

  return undefined;
};

/**
 * The same lookup, with the app's single post-authentication address as the answer when
 * nothing was supplied. Kept free of React context so the callbacks that run outside the
 * activation provider resolve the address exactly the same way as the screens inside it.
 */
export const resolvePostAuthRedirectTo = (
  sources: RedirectToSources,
): string => {
  const supplied = appSuppliedRedirectTo(sources);
  if (supplied) { return supplied; }

  // Nothing was supplied. The magic-link email wraps `redirectTo` in one opaque param
  // (VAF lib/handoffPayload.ts) immune to vendor-hop query fracture, so reaching here means
  // the app genuinely sent nothing — there is nothing to recover, just a default to pick.
  if (!reportedDefaultFallback) {
    reportedDefaultFallback = true;
    logError(
      '[PostAuthRedirect]',
      'no app address supplied; using the default post-auth callback',
      undefined,
      { level: 'warning' },
    );
  }

  return CANOPY_POST_AUTH_CALLBACK;
};

/** Test seam: the report-once latches are module state by design. */
export const resetRedirectReportingForTests = (): void => {
  reportedRejections.clear();
  reportedDefaultFallback = false;
};

/**
 * The hook form, for screens mounted inside the activation provider. It supplies the two
 * inputs the lookup needs — this screen's query string and the flow's state — and returns
 * the resolved address.
 */
export const usePostAuthRedirectTo = (): string => {
  const { search } = useLocation();
  const { state } = useActivation();

  return resolvePostAuthRedirectTo({ search, fromFlowState: state.redirectTo });
};

/**
 * The hook form of the relay lookup, for screens inside the provider that need to pass
 * the address to the auth service rather than navigate to it.
 */
export const useAppSuppliedRedirectTo = (): string | undefined => {
  const { search } = useLocation();
  const { state } = useActivation();

  return appSuppliedRedirectTo({ search, fromFlowState: state.redirectTo });
};
